// Lifted from useMatrixTableInfo; pure so basic-output can reuse without React/Jotai.
//
// Same "fat input, dumb function" pattern as buildSequentialTableInfo: the
// wrapping hook resolves Jotai context and sibling hooks and passes already-
// resolved values here. Basic-output (offline render) passes trivial defaults
// for the UI-driven inputs (sort, highlight, group-name editing) and reuses
// the same transform logic, so the rendered output cannot drift from the
// existing CustomizedAnalysisPage matrix layout.

import { type TFunction } from 'i18next'
import { produce } from 'immer'
import * as R from 'remeda'

import { type Question } from '~/hooks/useSurveyQuery'
import {
  calcHeatmapRatio,
  type CellInfo,
  compact,
  type DataDisplayType,
  getDiffKey,
  getDiffText,
  type Group,
  groupAllLabelI18nKey,
  type GroupAndAll,
  type IconName,
  NumFormat,
  QuestionOp,
  type RowInfo,
} from '~/utils'

import {
  type AnswerOption,
  type QuestionsDiff,
  type QuestionsDiffColumnKey,
  type RowData,
} from './common'
import { type MatrixColumnKey } from './matrix'

type MatrixTableInfoStyles = {
  answerText: string
  answerTextHeader: string
}

type MatrixAnswerRowData = RowData<string> & {
  answerOption: AnswerOption
}

export type MatrixBuildInput = {
  addHighlightProps: (key: string) => Partial<CellInfo>
  dataDisplayType: DataDisplayType
  diffMinMaxMap: Record<QuestionsDiffColumnKey, { max: number; min: number }>
  getSortIcon: (
    columnKey: QuestionsDiffColumnKey | string,
  ) => Extract<IconName, 'sort-asc' | 'sort-desc'> | 'blank'
  group: GroupAndAll
  groupNameMap: Record<Group, string>
  mapQuestionsDiff: <V>(
    mapFunc: (questionsDiff: QuestionsDiff, index: number) => V,
  ) => Record<QuestionsDiffColumnKey, V>
  mapSubQuestions: <V>(
    mapFunc: (subQuestion: Question) => V,
  ) => Record<string, V>
  setNameEditingGroup: (group: Group) => void
  // Sort callback applied per-sub-question. Identity for offline render.
  sortFunc: <T extends MatrixAnswerRowData>(rowsData: T[]) => T[]
  styles: MatrixTableInfoStyles
  t: TFunction
  tamRatio: number
  totalData: {
    counts: Record<GroupAndAll, number>
    ratios: Record<GroupAndAll, number>
  }
  visibleAnswersData: MatrixAnswerRowData[]
}

export const buildMatrixTableInfo = ({
  addHighlightProps,
  dataDisplayType,
  diffMinMaxMap,
  getSortIcon,
  group,
  groupNameMap,
  mapQuestionsDiff,
  mapSubQuestions,
  setNameEditingGroup,
  sortFunc,
  styles,
  t,
  tamRatio,
  totalData,
  visibleAnswersData,
}: MatrixBuildInput): RowInfo<MatrixColumnKey>[] =>
  compact([
    // ヘッダー
    {
      answerText: produce<CellInfo>(
        {
          align: 'center',
          className: styles.answerTextHeader,
          isBorderShownOnHoverWhenClickable: true,
          isColumnHeader: true,
          isHeader: true,
        },
        (draft) => {
          if (group === 'all') {
            draft.text = t(groupAllLabelI18nKey)
          } else {
            draft.group = group
            draft.text = groupNameMap[group]
            draft.onClick = () => {
              setNameEditingGroup(group)
            }
          }
        },
      ),
      ...mapSubQuestions((subQuestion) => ({
        align: 'center',
        icon: getSortIcon(subQuestion.question_id),
        isColumnHeader: true,
        isHeader: true,
        text: QuestionOp.toText(subQuestion),
      })),
      ...mapQuestionsDiff((questionsDiff): CellInfo => ({
        align: 'center',
        icon: getSortIcon(getDiffKey(questionsDiff)),
        isColumnHeader: true,
        isHeader: true,
        rowSpan: 2,
        text: getDiffText(questionsDiff),
      })),
    },
    // TOTAL
    {
      answerText: {
        align: 'right',
        isColumnHeader: true,
        isHeader: true,
        text: 'TOTAL',
      },
      ...mapSubQuestions(() => {
        const count = totalData.counts[group]
        const ratio = totalData.ratios[group]

        return {
          align: 'center',
          isColumnHeader: true,
          text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
        }
      }),
    },
    // 選択肢
    ...R.pipe(
      visibleAnswersData,
      sortFunc,
      R.map((answerData): RowInfo<MatrixColumnKey> => ({
        answerText: {
          align: 'right',
          className: styles.answerText,
          isHeader: true,
          text: answerData.answerOption.text,
          textCharacterLimit: 30,
        },
        ...mapSubQuestions((subQuestion): CellInfo => {
          const ratio = answerData.ratios[subQuestion.question_id]
          const count = answerData.counts[subQuestion.question_id]

          const key = `${answerData.answerOption.option}-${subQuestion.question_id}`

          return {
            align: 'center',
            ratio,
            text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
            ...addHighlightProps(key),
          }
        }),
        ...mapQuestionsDiff((questionsDiff): CellInfo => {
          const key = getDiffKey(questionsDiff)
          const ratio = answerData.ratios[key]
          const count = answerData.counts[key]
          const [text, copyValue] = NumFormat.countToPts(
            count,
            ratio,
            tamRatio,
            dataDisplayType,
          )
          const { max, min } = diffMinMaxMap[key]

          const highlightKey = `${answerData.answerOption.option}-${key}`

          return {
            align: 'center',
            copyValue,
            heatmap: true,
            ratio: calcHeatmapRatio(ratio, max, min),
            text,
            ...addHighlightProps(highlightKey),
          }
        }),
      })),
    ),
  ])
